import { Flex, Box } from 'rebass';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import useSWR, { useSWRPages } from 'swr';
// import { get } from 'lodash';
import { useWindowSize } from 'react-use';
import store from 'store';

import Loading from '../../../app/loading';
import CollectionStoryOptions from './options';
import StoryListItem from '../../../stories/list/item';
import { getResourcePath } from '../../api';
import getNextPaginatedPage from '../../../app/pagination/getNextPaginatedPage';
import LoadMoreButton from '../../../app/pagination/load-more-button';
import NewStoryTag from './NewStoryTag';
import Grid from 'app/grid/index';
import useHistory from 'app/hooks/history';
import { useDispatch } from 'react-redux';
import { togglefirstTimeUserFlowVisible } from 'stories/actions';
import { useFirstTimeUserFlowVisible } from 'stories/hooks/index';
import LetsBegin from 'stories/new/global/lets-begin';
import useEntity from 'entities/hooks/use-entity';

const CollectionStoriesBody = ({ data, chapter_id }) => {
  if (!data) {
    return (
      <Box p={4} css="width: 100%;">
        <Loading local />
      </Box>
    );
  }
  let { data: shares } = data.data.data.relationships['story-shares'] || data.data.data.relationships['stories'] || { data: [] };

  const included = data.data.included || [];

  shares = shares.map(share => {
    const storyShareDetails = included.find(option => option.id === share.id && option.type === share.type)
    const storyData = storyShareDetails?.relationships.story.data
    const storyDetails = included.find(option => option.id === storyData.id && option.type === storyData.type)

    return {
      ...(storyDetails || {}),
      ...share
    }
  })

  if (!shares.length) {
    return null
  }

  return shares.sort((a, b) => chapter_id ? new Date(a.attributes['began-on']) - new Date(b.attributes['began-on']) : new Date(b.attributes['updated-at']) - new Date(a.attributes['updated-at'])).map(share => (
    <StoryListItem hasTags key={share.id} id={share.id} type={share.type}>
      <CollectionStoryOptions
        id={share.id}
        type={share.type}
        collectionParams={{ id: data.data.data.id, type: data.data.data.type }}
      />
    </StoryListItem>
  ));
};
CollectionStoriesBody.defaultProps = {
  data: null,
};
CollectionStoriesBody.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({
      data: PropTypes.shape({
        relationships: PropTypes.shape({
          'story-shares': PropTypes.shape({
            data: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                type: PropTypes.string,
              })
            ),
          }),
        }),
      }),
    }),
  }),
};

const PaginatedCollectionStoriesBody = ({ id, type, tag }) => {
  const key = `${type}-${id}-${tag || 'recent'}`;
  const { pages, loadMore, isReachingEnd, isLoadingMore } = useSWRPages(
    key,
    ({ offset, withSWR }) => {
      const queryParams = { page: offset || 1, per: 10 };
      if (tag) {
        queryParams.tag_name = tag;
      }
      const path = getResourcePath({
        id,
        type,
        queryParams,
      });
      /* eslint-disable */
      const { data } = withSWR(useSWR(path));
      /* eslint-enable */
      return <CollectionStoriesBody key={key} data={data} />;
    },
    getNextPaginatedPage,
    [id]
  );
  return (
    <Flex flexWrap="wrap" px={3} pt={2}>
      {pages}
      {!isLoadingMore && !isReachingEnd && (
        <LoadMoreButton disabled={isLoadingMore || isReachingEnd} loadMore={loadMore} />
      )}
    </Flex>
  );
};

const CollectionStories = ({ id, type, isOnFreeTrial, chapter_id }) => {
  const { width } = useWindowSize();
  // const queryParams = { page: 1, per: 10 };
  const { push } = useHistory();
  const dispatch = useDispatch();
  const [canCreateStory, setCanCreateStory] = useState(true);

  const path = getResourcePath({
    ...{ id, type },
    // queryParams,
  });
  const { data } = useSWR(`${path}${chapter_id ? `?chapter_id=${chapter_id}` : ''}`);
  const isFirstTimeUser = useFirstTimeUserFlowVisible()

  let { entity: childJournal } = useEntity({ type: 'child-journals', id });
  const [showFTUE, setShowFTUE] = useState(false)

  const allowedStoriesCreation = childJournal?.['maximum-stories-permitted'];

  const itemCount = useMemo(() => {
    if (!data) return undefined;
    return data.data.data.meta['item-count'];
  }, [data]);

  const { data: shares } = data?.data?.data?.relationships['story-shares'] || data?.data?.data?.relationships['stories'] || { data: [] };

  const canUserCreateStory = useMemo(() => {
    if (!data) return undefined;
    return data.data.data.attributes?.paid;
  }, [data]);

  const canCreateAnotherStory = useMemo(() => {
    if (!data) return undefined;
    return data.data.data.attributes?.paid;
  }, [data]);

  const storyCount = useMemo(() => {
    if (!data) return undefined;
    return data.data.data.attributes['story-count'];
  }, [data]);
  const ftui_watched = store.get('ftui_watched');

  useEffect(() => {
    if (childJournal?.['has-baby-year-books-subscription'])
      setCanCreateStory(true);
    else if (storyCount >= allowedStoriesCreation && isOnFreeTrial) {
      setCanCreateStory(false);
    }
  }, [storyCount, childJournal?.paid, allowedStoriesCreation, isOnFreeTrial]);

  function openFtueFlow() {
    dispatch(togglefirstTimeUserFlowVisible(true))
    // push(`/child-journals/${id}/lets-begin`)
    setShowFTUE(true)
  }

  useEffect(() => {
    if (!ftui_watched && !window.location.pathname.includes('paywall'))
      openFtueFlow()
  }, [isFirstTimeUser])

  if (itemCount > 10) return <PaginatedCollectionStoriesBody id={id} type={type} />;

  return (<>
    {(showFTUE && canUserCreateStory) && <LetsBegin onClose={() => setShowFTUE(false)} setShowFTUE={setShowFTUE} childJournal={childJournal} />}
    <Grid
      gridTemplateColumns={`repeat(auto-fit, ${width < 600 ? 'minmax(48%, 48%)' : 'minmax(220px, 240px)'
        })`}
      gridGap={[1, 2]}
    >
      <NewStoryTag childJournal={childJournal} canCreateStory={canCreateStory} canCreateAnotherStory={canCreateAnotherStory} openFtueFlow={openFtueFlow} />
      <CollectionStoriesBody data={data} chapter_id={chapter_id} />
    </Grid>
  </>
  );
};

CollectionStories.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  chapter_id: PropTypes.string,
};

export default CollectionStories;
